/**
 * ConversationScheduledMessagesModal
 * -----------------------------------------------------------------------------
 * Modal exibido ao clicar no ícone de calendário do header do chat quando há
 * 2 ou mais mensagens agendadas (pendentes/falhou) para a conversa atual.
 * Reusa a tabela `ScheduledMessagesTable` (mesma usada na página global).
 *
 * Fonte de dados: RPC `list_scheduled_chat_messages` filtrando por
 * `conversation_id` e `status: ['pending','failed']`.
 *
 * @version 1.0.0
 * RELEASE: 2026-06-02 v1.0.0 - Versão inicial.
 */
import { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarClock, Loader2, Plus, RefreshCw } from 'lucide-react';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/lib/logger';
import { toast } from 'sonner';
import { usePermissions } from '@/hooks/usePermissions';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

import {
  ScheduledMessagesTable,
  type ScheduledMessageRow,
} from './ScheduledMessagesTable';
import { ScheduleMessageDialog } from '../ScheduleMessageDialog';
import { ScrollIndicators } from '@/components/ui/scroll-indicators';

export interface ConversationScheduledMessagesModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  conversationId: string;
  conversationContext?: {
    inboxId?: string;
    inboxName?: string;
    channelType?: string;
    /** v2.2.0 — última inbound (janela 24h WABA). */
    lastInboundAt?: string | null;
  };
}

export function ConversationScheduledMessagesModal({
  open,
  onOpenChange,
  conversationId,
  conversationContext,
}: ConversationScheduledMessagesModalProps) {
  const qc = useQueryClient();
  const { canEdit, canDelete, isAdmin } = usePermissions();

  const [editing, setEditing] = useState<ScheduledMessageRow | null>(null);
  const [creating, setCreating] = useState(false);
  const [confirmCancel, setConfirmCancel] =
    useState<ScheduledMessageRow | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  // -------------------------------------------------------------------------
  // Lista filtrada por conversa (apenas pendentes/falhou)
  // -------------------------------------------------------------------------
  const filters = useMemo(
    () => ({
      conversation_id: conversationId,
      // v1.1.0 — incluir mensagens já processadas para permitir visualização (somente leitura).
      status: ['pending', 'failed', 'sent', 'cancelled', 'skipped_replied'],
    }),
    [conversationId],
  );

  const { data, isFetching, refetch } = useQuery({
    queryKey: ['scheduled-messages-by-conversation', conversationId],
    enabled: open && !!conversationId,
    staleTime: 10_000,
    queryFn: async () => {
      const { data, error } = await supabase.rpc(
        'list_scheduled_chat_messages',
        {
          p_filters: filters,
          p_limit: 100,
          p_offset: 0,
        } as never,
      );
      if (error) throw error;
      return (data ?? []) as ScheduledMessageRow[];
    },
  });

  // Realtime: invalida quando reminder_queue muda para esta conversa.
  useEffect(() => {
    if (!open || !conversationId) return;
    const channel = supabase
      .channel(`conv-scheduled-${conversationId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'reminder_queue',
          filter: `conversation_id=eq.${conversationId}`,
        },
        () => {
          qc.invalidateQueries({
            queryKey: ['scheduled-messages-by-conversation', conversationId],
          });
        },
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [open, conversationId, qc]);

  const refreshAll = () => {
    qc.invalidateQueries({
      queryKey: ['scheduled-messages-by-conversation', conversationId],
    });
    qc.invalidateQueries({
      queryKey: ['scheduled-messages-count', conversationId],
    });
    qc.invalidateQueries({ queryKey: ['scheduled-messages'] });
  };

  // -------------------------------------------------------------------------
  // Ações
  // -------------------------------------------------------------------------
  const handleSendNow = async (item: ScheduledMessageRow) => {
    setActionLoading(item.id);
    try {
      const { error } = await supabase.rpc('send_scheduled_chat_message_now', {
        p_id: item.id,
      });
      if (error) throw error;
      toast.success('Envio disparado');
      refreshAll();
    } catch (err: any) {
      logger.error('[ConvScheduledModal] send_now error:', err);
      toast.error(err?.message || 'Erro ao disparar envio');
    } finally {
      setActionLoading(null);
    }
  };

  const handleCancel = async () => {
    if (!confirmCancel) return;
    setActionLoading(confirmCancel.id);
    try {
      const { error } = await supabase.rpc('cancel_scheduled_chat_message', {
        p_id: confirmCancel.id,
        p_reason: 'Cancelado pelo painel',
      });
      if (error) throw error;
      toast.success('Agendamento cancelado');
      setConfirmCancel(null);
      refreshAll();
    } catch (err: any) {
      logger.error('[ConvScheduledModal] cancel error:', err);
      toast.error(err?.message || 'Erro ao cancelar');
    } finally {
      setActionLoading(null);
    }
  };

  const canRowEdit = isAdmin || canEdit('scheduled_messages');
  const canRowDelete = isAdmin || canDelete('scheduled_messages');

  // Quando o usuário fecha o dialog interno (editar/criar), refaz a lista.
  const handleInnerDialogChange = (
    setter: (v: boolean) => void,
    isOpen: boolean,
  ) => {
    setter(isOpen);
    if (!isOpen) refreshAll();
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          className="max-w-5xl w-[95vw] h-[90dvh] md:h-auto md:max-h-[85dvh] flex flex-col p-0 gap-0 overflow-hidden"
        >
          {/* Capa premium padronizada (mesmo padrão do modal de anotações) */}
          <DialogHeader className="px-4 sm:px-6 py-3 sm:py-4 border-b bg-gradient-to-r from-primary/5 via-primary/3 to-transparent">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-start gap-3 min-w-0 pr-8">
                <div className="p-1.5 sm:p-2 bg-primary/10 rounded-lg shrink-0">
                  <CalendarClock className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />
                </div>
                <div className="flex flex-col min-w-0">
                  <DialogTitle className="truncate">Mensagens agendadas desta conversa</DialogTitle>
                  <DialogDescription className="text-xs line-clamp-2 sm:line-clamp-none">
                    Mensagens pendentes ou com falha agendadas para esta conversa. Clique para editar ou reenviar.
                  </DialogDescription>
                </div>
              </div>
              <div className="flex items-center gap-2 justify-end w-full sm:w-auto sm:pr-8">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => refetch()}
                  disabled={isFetching}
                  className="h-7 px-2 sm:px-3 gap-2"
                  aria-label="Atualizar"
                >
                  <RefreshCw
                    className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`}
                  />
                  <span className="hidden sm:inline">Atualizar</span>
                </Button>
                <Button
                  size="sm"
                  onClick={() => setCreating(true)}
                  className="h-7 px-2 sm:px-3 gap-2"
                >
                  <Plus className="h-4 w-4" />
                  Nova
                </Button>
              </div>
            </div>
          </DialogHeader>


          <ScrollIndicators
            className="flex-1 min-h-0 overflow-y-auto"
            scrollAmount={320}
            alwaysShowArrows
          >
            <div className="px-6 py-4">
              {isFetching && !data ? (
                <div className="flex items-center justify-center py-16 text-muted-foreground gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Carregando...
                </div>
              ) : !data || data.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 text-center text-muted-foreground">
                  <CalendarClock className="h-10 w-10 mb-3 opacity-40" />
                  <p className="text-sm">
                    Nenhuma mensagem agendada nesta conversa.
                  </p>
                </div>
              ) : (
                <ScheduledMessagesTable
                  items={data}
                  actionLoadingId={actionLoading}
                  canRowEdit={canRowEdit}
                  canRowDelete={canRowDelete}
                  showInboxColumn={false}
                  onEdit={(item) => setEditing(item)}
                  onSendNow={handleSendNow}
                  onCancel={(item) => setConfirmCancel(item)}
                  onRowClick={(item) => setEditing(item)}
                />
              )}
            </div>
          </ScrollIndicators>
        </DialogContent>
      </Dialog>

      {/* Dialog de edição */}
      {editing && (
        <ScheduleMessageDialog
          open={!!editing}
          onOpenChange={(v) =>
            handleInnerDialogChange(
              (vv) => !vv && setEditing(null),
              v,
            )
          }
          scheduledId={editing.id}
          conversationContext={conversationContext}
          initial={{
            scheduledAt: editing.remind_at,
            text:
              editing.message_payload?.text ||
              editing.message_payload?.caption ||
              '',
            cancelOnReply: editing.cancel_on_reply,
            internalNote: editing.internal_note ?? '',
            linkedMacroRuleId: editing.linked_macro_rule_id ?? null,
            linkedMacroMode:
              (editing.linked_macro_execution_mode as any) ?? null,
          }}
          onSuccess={() => {
            setEditing(null);
            refreshAll();
          }}
          readOnly={
            !canRowEdit ||
            (editing.status !== 'pending' && editing.status !== 'failed')
          }
        />
      )}

      {/* Dialog de criação */}
      {creating && (
        <ScheduleMessageDialog
          open={creating}
          onOpenChange={(v) => {
            setCreating(v);
            if (!v) refreshAll();
          }}
          conversationId={conversationId}
          conversationContext={conversationContext}
          onSuccess={() => {
            setCreating(false);
            refreshAll();
          }}
        />
      )}

      {/* Confirm cancel */}
      <AlertDialog
        open={!!confirmCancel}
        onOpenChange={(v) => !v && setConfirmCancel(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancelar agendamento?</AlertDialogTitle>
            <AlertDialogDescription>
              A mensagem programada para{' '}
              {confirmCancel &&
                format(
                  new Date(confirmCancel.remind_at),
                  "dd/MM/yyyy 'às' HH:mm",
                  { locale: ptBR },
                )}{' '}
              não será enviada. Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Voltar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleCancel}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Sim, cancelar envio
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
